/**
 * PRIVACY: Geen user-input naar server, localStorage (behalve onboarding-flag),
 * externe APIs. PortalAuth verify via SDK; minimale Umami analytics (tool_open/close).
 */
import { useCallback, useState } from "react";
import {
  ToolLayout,
  ToolTabs,
  PaywallModal,
  Onboarding,
  useAccess,
  hasCompletedOnboarding,
  setOnboardingComplete,
  ShareLinksManager,
  demoShareLinksActAvontuur,
  type OnboardingSlide,
} from "@jari-tools/shared";
import { getAdventures } from "./content";
import { Game } from "./components/Game";

const TOOL_SLUG = import.meta.env.VITE_TOOL_SLUG || "act-avontuur";
const TOOL_NAME = "ACT Avontuur: Defusie";
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL;

const ONBOARDING_SLIDES: OnboardingSlide[] = [
  {
    title: "ACT defusie",
    body: "Laat kinderen (10+) een speelse manier kennismaken met ACT defusie. In het spel kunnen ze ervaren hoe serieus we onze gedachtes soms nemen en hoe deze invloed kunnen hebben op keuzes die we maken. Vervolgens ontdekken ze hoe ze meer ruimte kunnen maken voor wat écht belangrijk is.",
  },
  {
    title: "Gratis en betaald",
    body: "Je kunt één missie gratis spelen: De ruimte. Die vinden we zelf ook het leukst. De andere twee missies zitten in de betaalde versie.",
  },
];

const ADVENTURES = getAdventures();

const SHARE_CONFIG_FIELDS = [
  {
    name: "adventureId",
    label: "Avontuur",
    type: "select" as const,
    required: true,
    options: ADVENTURES.map((a) => ({ value: a.id, label: a.label })),
  },
];

type Tab = "spel" | "delen";

export function App() {
  const { access, name, loading, error, errorCode, isPaid, isGuest, toolId } = useAccess();
  const [tab, setTab] = useState<Tab>("spel");
  const [showOnboarding, setShowOnboarding] = useState(
    () => !hasCompletedOnboarding(TOOL_SLUG),
  );
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState("deze functie");

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(TOOL_SLUG);
    setShowOnboarding(false);
  }, []);

  const onPaywall = useCallback((feature: string) => {
    setPaywallFeature(feature);
    setPaywallOpen(true);
  }, []);

  return (
    <>
      <ToolLayout
        toolName={TOOL_NAME}
        access={access}
        userName={name}
        loading={loading}
        error={error}
        errorCode={errorCode}
        portalUrl={PORTAL_URL}
        isGuest={isGuest}
        toolId={toolId}
        toolSlug={TOOL_SLUG}
        onShowOnboarding={() => setShowOnboarding(true)}
      >
        <ToolTabs
          ariaLabel="ACT Avontuur secties"
          activeId={tab}
          onSelect={(id) => setTab(id as Tab)}
          tabs={[
            { id: "spel", label: "Spel" },
            { id: "delen", label: "Delen" },
          ]}
        />

        <div
          id="jari-tool-panel-spel"
          role="tabpanel"
          aria-labelledby="jari-tool-tab-spel"
          hidden={tab !== "spel"}
        >
          {tab === "spel" ? (
          <Game
            adventures={ADVENTURES}
            isPaid={isPaid}
            portalUrl={PORTAL_URL}
            onPaywall={onPaywall}
          />
          ) : null}
        </div>

        <div
          id="jari-tool-panel-delen"
          role="tabpanel"
          aria-labelledby="jari-tool-tab-delen"
          hidden={tab !== "delen"}
        >
          {tab === "delen" ? (
          <ShareLinksManager
            toolSlug={TOOL_SLUG}
            access={access}
            configFields={SHARE_CONFIG_FIELDS}
            demoLinks={demoShareLinksActAvontuur()}
            renderStatus={(data) =>
              typeof data?.adventureLabel === "string" ? data.adventureLabel : null
            }
            renderResult={(data) =>
              typeof data?.adventureLabel === "string" ? data.adventureLabel : null
            }
          />
          ) : null}
        </div>
      </ToolLayout>

      <Onboarding
        open={showOnboarding}
        slides={ONBOARDING_SLIDES}
        onComplete={completeOnboarding}
        onSkip={completeOnboarding}
      />

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        featureTitle={paywallFeature}
        portalUrl={PORTAL_URL}
      />
    </>
  );
}
